/* eslint-disable no-case-declarations */
import dayjs from 'dayjs';
import { arrayMove } from '@dnd-kit/sortable';
import { DeleteOutlined } from '@ant-design/icons';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQuery as useSubjectsQuery } from 'react-query';
import {
  Row,
  Col,
  Card,
  Spin,
  Flex,
  Form,
  Space,
  Input,
  Radio,
  Table,
  Select,
  Drawer,
  Button,
  isEmpty,
  Dropdown,
  useSnackbar,
} from 'design-system';

import { formatValueByUnit } from 'src/utils/format-number';
import { getSearchObjective } from 'src/utils/getMetricScope';
import { SystemSettingKey, getSystemSettingValue } from 'src/utils/systemSetting';

import { getUserProfile } from 'src/services/user';
import { useConfigStore } from 'src/stores/config';
import { CalendarConfig } from 'src/services/config';
import appViewEmpty from 'src/assets/icons/appViewEmpty.png';
import { queryKeys, mutationKeys } from 'src/constants/queryKeys';
import { Subject, TypeOfChange, getAllSubjects } from 'src/services/subject';
import { calculateSequence, getSequenceByDate, getSubjectDisplayName } from 'src/utils';
import { Plan, searchPlan, PlanAccumulation, getPlanAccumulation } from 'src/services/plan';
import {
  MetricType,
  CustomDashboard,
  DashboardSetting,
  getEnabledDashboard,
  createEnabledDashboard,
  updateEnabledDashboard,
  DashboardSettingRateType,
} from 'src/services/dashboard';

import GaugeChart from 'src/components/GaugeChart';

import TargetCyclePicker, {
  TargetCycle,
} from 'src/sections/objectivesManagement/target-cycle-picker';

import { FrequencyType } from 'src/types/common';
import type { DepartmentItem } from 'src/types/category';

import styles from './index.module.css';
import { paths } from '../../../../routes/paths';
import { useRouter } from '../../../../routes/hooks';
import { useAuthContext } from '../../../../auth/hooks';
import { PrivilegeType } from '../../../../services/role';
import { useBoolean } from '../../../../hooks/use-boolean';
import { Department } from '../../../../services/template';
import SectionTitle from '../../../../components/section-title';
import { DndProvider } from '../../../reportTemplate/components';
import { DraggableItem } from '../../../../components/draggableItem';
import subjectsData from '../../../../assets/templates/subjects.json';
import { useDepartmentsByPrivilege } from '../../../../hooks/use-departments-by-privilege';
import MetricCodeButton from '../../../../components/metric-code-button/metric-code-button';

// 定义科目数据类型
interface SubjectItem {
  科目: string;
  英文: string;
  缩写: string;
}
// import {
//   Bar, Pie, Line, Cell, Area, XAxis, YAxis, Legend, Tooltip, BarChart, PieChart, LineChart, CartesianGrid, ComposedChart, ResponsiveContainer
// } from 'recharts';

// 销售数据模拟
const salesData = [
  { name: '1月', 本年: 4000, 上年: 2400 },
  { name: '2月', 本年: 3000, 上年: 1398 },
  { name: '3月', 本年: 2000, 上年: 9800 },
  { name: '4月', 本年: 2780, 上年: 3908 },
  { name: '5月', 本年: 1890, 上年: 4800 },
  { name: '6月', 本年: 2390, 上年: 3800 },
  { name: '7月', 本年: 3490, 上年: 4300 },
];

// 毛利率数据模拟
const profitData = [
  { name: '1月', 毛利率: 25 },
  { name: '2月', 毛利率: 28 },
  { name: '3月', 毛利率: 26 },
  { name: '4月', 毛利率: 30 },
  { name: '5月', 毛利率: 29 },
  { name: '6月', 毛利率: 32 },
  { name: '7月', 毛利率: 35 },
];

// 堆叠柱状图数据
const stackedBarData = [
  { name: '1月', A组: 35, B组: 100, C组: 80 },
  { name: '2月', A组: 25, B组: 70, C组: 40 },
  { name: '3月', A组: 20, B组: 20, C组: 55 },
  { name: '4月', A组: 85, B组: 35, C组: 95 },
];

// 饼图数据
const pieData = [
  { name: '1月', value: 30, color: '#FFCC33' },
  { name: '2月', value: 25, color: '#99CCFF' },
  { name: '3月', value: 20, color: '#6699FF' },
  { name: '4月', value: 25, color: '#334C80' },
];

// 雷达图数据
const radarData = [
  { subject: '销售额', A: 120, B: 110, C: 90, fullMark: 150 },
  { subject: '利润率', A: 98, B: 130, C: 85, fullMark: 150 },
  { subject: '客户数', A: 86, B: 130, C: 70, fullMark: 150 },
  { subject: '增长率', A: 99, B: 100, C: 65, fullMark: 150 },
  { subject: '回款率', A: 85, B: 90, C: 110, fullMark: 150 },
  { subject: '市场份额', A: 65, B: 85, C: 100, fullMark: 150 },
];

// 销售表格数据
const tableData = [
  {
    key: '1',
    product: '产品A',
    sales: 1243500,
    lastYearSales: 987600,
    growth: 25.9,
    profit: 432500,
    profitRate: 34.8,
  },
  {
    key: '2',
    product: '产品B',
    sales: 985600,
    lastYearSales: 876400,
    growth: 12.5,
    profit: 295600,
    profitRate: 30.0,
  },
  {
    key: '3',
    product: '产品C',
    sales: 673400,
    lastYearSales: 598700,
    growth: 12.5,
    profit: 168350,
    profitRate: 25.0,
  },
];

// 表格列定义
const columns = [
  {
    title: '产品',
    dataIndex: 'product',
    key: 'product',
  },
  {
    title: '本期销售额',
    dataIndex: 'sales',
    key: 'sales',
    render: (text: number) => formatValueByUnit(text, '￥'),
  },
  {
    title: '同期销售额',
    dataIndex: 'lastYearSales',
    key: 'lastYearSales',
    render: (text: number) => formatValueByUnit(text, '￥'),
  },
  {
    title: '同比增长',
    dataIndex: 'growth',
    key: 'growth',
    render: (text: number) => `${text}%`,
  },
  {
    title: '毛利额',
    dataIndex: 'profit',
    key: 'profit',
    render: (text: number) => formatValueByUnit(text, '￥'),
  },
  {
    title: '毛利率',
    dataIndex: 'profitRate',
    key: 'profitRate',
    render: (text: number) => `${text}%`,
  },
];

// 组合图表数据
const composedChartData = [
  { name: '1月', 销售额: 590, 销售量: 800, 同比增长: 30, 目标达成率: 65 },
  { name: '2月', 销售额: 868, 销售量: 967, 同比增长: 25, 目标达成率: 78 },
  { name: '3月', 销售额: 1397, 销售量: 1098, 同比增长: 42, 目标达成率: 90 },
  { name: '4月', 销售额: 1480, 销售量: 1200, 同比增长: 35, 目标达成率: 85 },
  { name: '5月', 销售额: 1520, 销售量: 1108, 同比增长: 20, 目标达成率: 79 },
  { name: '6月', 销售额: 1400, 销售量: 680, 同比增长: -8, 目标达成率: 70 },
  { name: '7月', 销售额: 1380, 销售量: 730, 同比增长: -15, 目标达成率: 68 },
];

// 经营指标代码表格数据
const metricCodeData = subjectsData.map((item: SubjectItem, index: number) => ({
  key: (index + 1).toString(),
  chineseName: item.科目,
  name: item.英文,
  code: item.缩写,
}));

// 经营指标代码表格列定义
const metricCodeColumns = [
  {
    title: '科目',
    dataIndex: 'chineseName',
    key: 'chineseName',
    width: 200,
  },
  {
    title: '英文',
    dataIndex: 'name',
    key: 'name',
    width: 250,
  },
  {
    title: '缩写',
    dataIndex: 'code',
    key: 'code',
    width: 100,
  },
];

// 编码规则数据
const codingRuleData = [
  {
    key: '1',
    category: '目标',
    rule: 'BP+年份+Y/Q/M/W+第xx季度/月度+科目代码+唯一序号；\nY：报表类型=年报表\nQ：报表类型=季度报表\nM：报表类型=月报表\nW：报表类型=周报表',
  },
  { key: '2', category: '差异分析', rule: 'PA+年份+Y/Q/M/W+第xx季度/月度+科目代码+唯一序号' },
  { key: '3', category: '项目', rule: 'PJ+年份+Y/Q/M/W+第xx季度/月度+科目代码+唯一序号' },
  { key: '4', category: '行动计划', rule: 'AC+年份+Y/Q/M/W+第xx季度/月度+科目代码+唯一序号' },
];

// 编码规则表格列定义
const codingRuleColumns = [
  {
    title: '类目',
    dataIndex: 'category',
    key: 'category',
    width: 120,
  },
  {
    title: '编码规则',
    dataIndex: 'rule',
    key: 'rule',
    render: (text: string) => <div style={{ whiteSpace: 'pre-line' }}>{text}</div>,
  },
];

// 固定算法公式数据
const algorithmFormulaData = [
  { key: '1', formulaName: '完成率', calculationFormula: '实际值 / 计划值 * 100%' },
  { key: '2', formulaName: '达成率', calculationFormula: '(计划值-实际值) /计划值 + 100%' },
  {
    key: '3',
    formulaName: '环比增长率',
    calculationFormula: '[ (本期数 - 上期数) / 上期数 ] × 100%',
  },
  {
    key: '4',
    formulaName: '同比增长率',
    calculationFormula: '[ (本期数 - 上年同期数) / 上年同期数 ] × 100%',
  },
  {
    key: '5',
    formulaName: '行动计划完成率',
    calculationFormula:
      '（ 1-（已完成行动计划数 - 行动计划数 + 已取消行动计划数）/ 行动计划数））* 100%',
  },
  { key: '6', formulaName: '同比增长率', calculationFormula: '(计划值-实际值) /计划值 *100%' },
];

// 固定算法公式表格列定义
const algorithmFormulaColumns = [
  {
    title: '公式名称',
    dataIndex: 'formulaName',
    key: 'formulaName',
    width: 150,
  },
  {
    title: '计算公式',
    dataIndex: 'calculationFormula',
    key: 'calculationFormula',
  },
];

export default function OverviewAppView() {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { user } = useAuthContext();

  const { calendarConfig, systemSettingConfig } = useConfigStore();
  const statisticFinishedAt = getSystemSettingValue(
    systemSettingConfig,
    SystemSettingKey.PLAN_YEARLY_MERGE_FINISHED_AT,
    '-'
  );
  const [form] = Form.useForm();
  const disable = useMemo(() => !user?.permissions[PrivilegeType.DASHBOARD]?.write, [user]);
  const [dashboardData, setDashboardData] = useState<CustomDashboard | null>(null);
  // 使用dayjs来处理日期
  const [selectedTargetCycle, setSelectedTargetCycle] = useState<TargetCycle>({
    type: FrequencyType.Monthly,
    date: dayjs(),
  });

  // 部门id
  const [departmentId, setDepartmentId] = useState<string | number | undefined>(undefined);
  // 存储searchPlan返回的数据
  const [planData, setPlanData] = useState<Plan[]>([]);
  const [planAccumulations, setPlanAccumulations] = useState<PlanAccumulation[]>([]);
  const formOpen = useBoolean();
  const metricCodeDrawerOpen = useBoolean();

  const handleTargetCycleChange = (targetCycle: TargetCycle) => {
    setSelectedTargetCycle(targetCycle);
  };

  // 按 PLAN 权限获取可选部门
  const { departments, isLoading: isDepartmentsLoading } = useDepartmentsByPrivilege(
    PrivilegeType.PLAN,
    { staleTime: 0 }
  );
  // 获取当前用户
  const { data: userProfile, isLoading: isUserLoading } = useQuery({
    queryKey: [queryKeys.getUserProfile],
    queryFn: getUserProfile,
    staleTime: 0,
  });

  // 获取所有科目
  const { data: subjectsData, isLoading: isSubjectsLoading } = useSubjectsQuery({
    queryKey: [queryKeys.getAllSubjects],
    queryFn: () => getAllSubjects({ page: 1, size: 9999 }),
    staleTime: 0,
  });

  // 根据科目名查找科目对象
  const findSubjectByName = (name: string): Subject | undefined =>
    subjectsData?.items?.find((s: Subject) => s.name === name);

  // 初始化默认部门
  useEffect(() => {
    if (userProfile && userProfile.category && departments.length > 0) {
      setDepartmentId(userProfile.category.id);
      // setDepartmentId('624603e1-9e37-4db1-b1e9-49d393bedc26');
    } else if (departments.length > 0 && departmentId === undefined) {
      setDepartmentId(departments[0].id);
    }
  }, [userProfile, departments]);

  // 根据不同周期类型和选择的日期生成标题
  const generateTitle = () => {
    if (!selectedTargetCycle?.date) {
      return '';
    }
    const deptName = departments.find((d: DepartmentItem) => d.id === departmentId)?.name || '';

    if (selectedTargetCycle.type === FrequencyType.Yearly) {
      return `${deptName}${selectedTargetCycle.date.format('YYYY')}年经营业绩分析`;
    }
    if (selectedTargetCycle.type === FrequencyType.Quarterly) {
      const quarter = calculateSequence('quarter', selectedTargetCycle.date, calendarConfig);
      return `${deptName}${selectedTargetCycle.date.format('YYYY')}年第${quarter}季度经营业绩分析`;
    }
    if (selectedTargetCycle.type === FrequencyType.Weekly) {
      const week = calculateSequence('week', selectedTargetCycle.date, calendarConfig);
      return `${deptName}${selectedTargetCycle.date.format('YYYY')}年第${week}周经营业绩分析`;
    }
    return `${deptName}${selectedTargetCycle.date.format('YYYY')}年${selectedTargetCycle.date.format('M')}月经营业绩分析`;
  };

  const loading = useBoolean();
  const handleGetMetricData = async (
    departmentId: string,
    frequencyType: FrequencyType,
    dashboard: CustomDashboard,
    date: dayjs.Dayjs
  ) => {
    loading.onTrue();
    try {
      if (calendarConfig) {
        const payload = getSearchObjective(departmentId, dashboard.settings, date, calendarConfig);
        const data = await searchPlan({
          ...payload,
          sequences: payload.sequences ? payload.sequences.slice(0, 1) : undefined,
        });
        const kpiSubjectIds = dashboard.settings
          .filter((item) => item.type === MetricType.KPI_SUM)
          .map((item) => item.subjectId);
        if (kpiSubjectIds.length) {
          const currentAccumulationData = await getPlanAccumulation({
            sequences: payload.sequences,
            startYear: date.year(),
            frequencyType,
            subjectIds: kpiSubjectIds,
            departmentId,
          });
          setPlanAccumulations(currentAccumulationData);
        }
        setPlanData(data);
      }
    } catch (error) {
      // 错误处理
      enqueueSnackbar(`该部门目前暂无设置驾驶舱`, { variant: 'error' });
    }
    loading.onFalse();
  };

  const handleGetDashboard = async (departmentId: string, frequencyType: FrequencyType) => {
    try {
      loading.onTrue();
      const dashboard = await getEnabledDashboard({ departmentId, frequencyType });

      if (dashboard?.settings?.length && selectedTargetCycle.type && selectedTargetCycle.date) {
        await handleGetMetricData(
          departmentId as string,
          selectedTargetCycle.type,
          dashboard,
          selectedTargetCycle.date
        );
      } else {
        loading.onFalse();
      }
      setDashboardData(dashboard);
    } catch (error) {
      // 错误处理
      enqueueSnackbar(`该部门目前暂无设置驾驶舱`, { variant: 'error' });
      setDashboardData(null);
      loading.onFalse();
    }
  };
  const getTemplate = useCallback(() => {
    if (
      departmentId &&
      selectedTargetCycle.date &&
      selectedTargetCycle.type &&
      user?.permissions[PrivilegeType.DASHBOARD]?.read
    ) {
      handleGetDashboard(departmentId as string, selectedTargetCycle.type);
    }
  }, [departmentId, selectedTargetCycle.date, selectedTargetCycle.type, user]);
  const createEnabledDashboardMutation = useMutation({
    mutationKey: [mutationKeys.createEnabledDashboard],
    mutationFn: createEnabledDashboard,
    onSuccess: () => {
      enqueueSnackbar('添加成功', { variant: 'success' });
      getTemplate();
      formOpen.onFalse();
    },
  });
  const updateEnabledDashboardMutation = useMutation({
    mutationKey: [mutationKeys.updateEnabledDashboard],
    mutationFn: updateEnabledDashboard,
    onSuccess: () => {
      enqueueSnackbar('操作成功', { variant: 'success' });
      getTemplate();
      formOpen.onFalse();
    },
  });
  useEffect(() => {
    getTemplate();
    setPlanData([]);
    setPlanAccumulations([]);
  }, [getTemplate]);

  const extractMetricData = (setting: DashboardSetting, date: dayjs.Dayjs) => {
    const subject = subjectsData?.items.find((s: Subject) => s.id === setting.subjectId);
    const selectedSequence = getSequenceByDate(
      date,
      setting.frequencyType,
      calendarConfig as CalendarConfig
    );
    // const currentSequence = getSequenceByDate(
    //   dayjs(),
    //   setting.frequencyType,
    //   calendarConfig as CalendarConfig
    // );
    switch (setting.type) {
      case MetricType.KPI:
        const plan = planData?.find(
          (n) =>
            n.subject?.id === setting.subjectId &&
            n.frequencyType === setting.frequencyType &&
            n.sequence === selectedSequence
        );
        const target = plan?.target;
        const currentFixed = plan?.operationalStatement?.currentFixed;
        const current = plan?.operationalStatement?.current;
        const actual = currentFixed !== null && currentFixed !== undefined ? currentFixed : current;
        return {
          subject,
          data: [target, actual],
          // KPI 场景不使用 operationalStatementCurrent 回退
          actualFromOperationalStatementCurrent: false,
        };
      case MetricType.KPI_SUM:
        const plans = planAccumulations?.filter(
          (n) => n.subjectId === setting.subjectId && n.frequencyType === setting.frequencyType
          // && n.sequence <= currentSequence
        );

        // 从 planData 中获取对应的详细数据以获取 currentFixed 信息
        const relatedPlans = planData?.filter(
          (n) => n.subject?.id === setting.subjectId && n.frequencyType === setting.frequencyType
        );

        const targetSum = plans.reduce((acc, p) => acc + (p.target ?? 0), 0);

        // 计算实际值：优先使用 currentFixed，如果为 null 则使用 current
        let actualSum: number | null = null;
        let actualFromOperationalStatementCurrent = false;
        if (relatedPlans && relatedPlans.length > 0) {
          let sum = 0;
          let hasValidValue = false;

          relatedPlans.forEach((p) => {
            const currentFixed = p.operationalStatement?.currentFixed;
            const current = p.operationalStatement?.current;
            const value =
              currentFixed !== null && currentFixed !== undefined ? currentFixed : current;

            if (value !== null && value !== undefined) {
              sum += value;
              hasValidValue = true;
            }
          });

          actualSum = hasValidValue ? sum : null;
          actualFromOperationalStatementCurrent = false;
        } else {
          // 如果没有详细数据，回退到使用 operationalStatementCurrent
          let sum = 0;
          let hasValidValue = false;

          plans.forEach((p) => {
            if (
              p?.operationalStatementCurrent !== null &&
              p?.operationalStatementCurrent !== undefined
            ) {
              sum += p.operationalStatementCurrent;
              hasValidValue = true;
            }
          });

          actualSum = hasValidValue ? sum : null;
          actualFromOperationalStatementCurrent = true;
        }

        return {
          subject,
          data: [targetSum, actualSum],
          actualFromOperationalStatementCurrent,
        };
      default:
        return { subject, data: [], actualFromOperationalStatementCurrent: false };
    }
  };
  const isLoading = useMemo(
    () =>
      loading.value ||
      updateEnabledDashboardMutation.isLoading ||
      createEnabledDashboardMutation.isLoading,
    [
      loading.value,
      updateEnabledDashboardMutation.isLoading,
      createEnabledDashboardMutation.isLoading,
    ]
  );
  const renderMetric = useCallback(
    (setting: DashboardSetting) => {
      if (!selectedTargetCycle.date) {
        return;
      }
      const { data, subject, actualFromOperationalStatementCurrent } = extractMetricData(
        setting,
        selectedTargetCycle.date
      );
      const currentSequence = getSequenceByDate(
        selectedTargetCycle.date,
        setting.frequencyType,
        calendarConfig as CalendarConfig
      );
      if (isLoading) {
        return null;
      }
      const target = data[0] as number;

      const actual = data[1] as number;
      const targetIsEmpty = isEmpty(target);
      const actualIsEmpty = isEmpty(actual);
      let percent = 0;
      const hasEmpty = targetIsEmpty || actualIsEmpty;
      if (!hasEmpty) {
        if (setting.formulaType === DashboardSettingRateType.AchievementRate) {
          // 达成率公式：(计划值 - 实际值) / 计划值 + 100%
          percent = ((target - actual) / target + 1) * 100;
        } else {
          // 完成率公式：实际值 / 计划值 * 100%
          percent = (actual / target) * 100;
        }
      }
      // eslint-disable-next-line no-restricted-globals
      percent = isNaN(percent) ? 0 : percent;
      // 判断是否为负指标（支出类科目）
      const isNegativeMetric = subject?.typeOfChange === TypeOfChange.Expense;
      switch (setting.type) {
        case MetricType.KPI:
          return (
            <Flex gap={16} align="center" style={{ width: '100%' }}>
              <GaugeChart
                percent={percent}
                title={setting.name}
                width={100}
                isEmpty={hasEmpty}
                wrapWidth="auto"
                isNegative={isNegativeMetric}
              />

              <div>
                <div style={{ fontSize: 12, color: '#525866', marginTop: 4 }}>
                  目标：
                  {target !== undefined && target !== null && subject
                    ? formatValueByUnit(target, subject.measurementUnit)
                    : '-'}
                </div>
                <div style={{ fontSize: 12, color: '#525866' }}>
                  实际：
                  {actual !== undefined && actual !== null && subject
                    ? formatValueByUnit(actual, subject.measurementUnit)
                    : '-'}
                </div>
              </div>
            </Flex>
          );
        case MetricType.KPI_SUM:
          return (
            <Flex gap={16} align="center" style={{ width: '100%' }}>
              <GaugeChart
                percent={percent}
                isEmpty={hasEmpty}
                title={`${setting.name}(1-${currentSequence})${setting.frequencyType === FrequencyType.Monthly ? '月' : ''}`}
                width={100}
                wrapWidth="auto"
                isNegative={isNegativeMetric}
              />

              <div>
                <div style={{ fontSize: 12, color: '#525866', marginTop: 4 }}>
                  目标：
                  {!targetIsEmpty && subject
                    ? formatValueByUnit(target, subject.measurementUnit)
                    : '-'}
                </div>
                <div style={{ fontSize: 12, color: '#525866' }}>
                  实际：
                  {!actualIsEmpty && subject
                    ? formatValueByUnit(actual, subject.measurementUnit)
                    : '-'}
                </div>
              </div>
            </Flex>
          );
        default:
          return <div />;
      }
    },
    [planData, selectedTargetCycle.date, calendarConfig, isLoading, planAccumulations]
  );
  const moveItem = (dragIndex: number, hoverIndex: number) => {
    updateEnabledDashboardMutation.mutate({
      ...dashboardData,
      settings: arrayMove(dashboardData?.settings || [], dragIndex, hoverIndex),
    } as CustomDashboard);
  };

  return (
    <>
      <Drawer
        footer={
          <Flex justify="flex-end" gap={12}>
            <Button onClick={formOpen.onFalse}>取消</Button>
            <Button
              type="primary"
              loading={
                createEnabledDashboardMutation.isLoading || updateEnabledDashboardMutation.isLoading
              }
              onClick={() => {
                form.validateFields().then((res) => {
                  if (!res?.settings) {
                    enqueueSnackbar('请添加仪表盘', { variant: 'warning' });
                  }

                  const frequencyType = selectedTargetCycle.type || FrequencyType.Monthly;
                  const value = {
                    settings: res.settings.map((setting: DashboardSetting) => ({
                      ...setting,
                      frequencyType,
                    })) as DashboardSetting[],
                    frequencyType,
                    departments: [{ id: departmentId } as Department],
                  };
                  if (dashboardData) {
                    updateEnabledDashboardMutation.mutate({ ...dashboardData, ...value });
                  } else {
                    createEnabledDashboardMutation.mutate(value);
                  }
                });
              }}
            >
              保存
            </Button>
          </Flex>
        }
        open={formOpen.value}
        onClose={formOpen.onFalse}
        width={560}
        title="仪表盘设置"
      >
        <Form form={form} colon={false} labelCol={{ span: 6 }}>
          <Form.List name="settings">
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', rowGap: 16, flexDirection: 'column' }}>
                {fields.map((field) => (
                  <Flex
                    style={{ width: '512px' }}
                    justify="space-between"
                    align="start"
                    key={field.key}
                  >
                    <div style={{ width: '482px', padding: '12px', background: '#F7F9FC' }}>
                      <div
                        style={{
                          color: '#292C33',
                          fontSize: '14px',
                          fontWeight: '600',
                          marginBottom: '12px',
                        }}
                      >
                        仪表盘{field.name + 1}
                      </div>
                      <Form.Item
                        label="仪表盘名称"
                        required
                        rules={[{ required: true, message: '请输入仪表盘名称' }]}
                        name={[field.name, 'name']}
                      >
                        <Input showCount maxLength={8} />
                      </Form.Item>
                      <Form.Item
                        label="数据显示范围"
                        required
                        name={[field.name, 'type']}
                        rules={[{ required: true, message: '请选择数据显示范围' }]}
                      >
                        <Radio.Group>
                          <Radio value={MetricType.KPI}>只看当前驾驶舱数据</Radio>
                          <Radio
                            disabled={selectedTargetCycle.type !== FrequencyType.Monthly}
                            value={MetricType.KPI_SUM}
                          >
                            看1~N月数据
                          </Radio>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item
                        label="计算公式"
                        name={[field.name, 'formulaType']}
                        initialValue={DashboardSettingRateType.CompletionRate}
                        required
                        rules={[{ required: true, message: '请选择计算公式' }]}
                      >
                        <Select
                          placeholder="请选择计算公式"
                          options={[
                            {
                              label: '完成率',
                              value: DashboardSettingRateType.CompletionRate,
                            },
                            {
                              label: '达成率',
                              value: DashboardSettingRateType.AchievementRate,
                            },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item
                        shouldUpdate={(prevValues, currentValues) => {
                          const prevFormulaType = prevValues?.settings?.[field.name]?.formulaType;
                          const currentFormulaType =
                            currentValues?.settings?.[field.name]?.formulaType;
                          return prevFormulaType !== currentFormulaType;
                        }}
                      >
                        {({ getFieldValue }) => {
                          const formulaType = getFieldValue([
                            'settings',
                            field.name,
                            'formulaType',
                          ]);
                          const formulaText =
                            formulaType === DashboardSettingRateType.CompletionRate
                              ? '实际值 / 计划值 * 100%'
                              : formulaType === DashboardSettingRateType.AchievementRate
                                ? '(计划值 - 实际值) / 计划值 + 100%'
                                : '';

                          return formulaText ? (
                            <div
                              style={{
                                marginLeft: '120px', // 与表单标签宽度对齐
                                fontSize: '14px',
                                color: '#666',
                              }}
                            >
                              {formulaText}
                            </div>
                          ) : null;
                        }}
                      </Form.Item>
                      <Form.Item
                        label="科目"
                        required
                        name={[field.name, 'subjectId']}
                        rules={[{ required: true, message: '请选择科目' }]}
                      >
                        <Select
                          placeholder="科目"
                          allowClear
                          options={(subjectsData?.items || []).map((subject) => ({
                            label: getSubjectDisplayName(subject, subjectsData?.items || [], ' / '),
                            value: subject.id,
                          }))}
                          showSearch
                          loading={isSubjectsLoading}
                          optionFilterProp="label"
                        />
                      </Form.Item>
                    </div>
                    <DeleteOutlined
                      style={{ color: '#105FC8', width: '12.25px', height: '12.25px' }}
                      onClick={() => remove(field.name)}
                    />
                  </Flex>
                ))}

                <Button
                  style={{ width: '88px', padding: 0 }}
                  type="link"
                  onClick={() => {
                    if (selectedTargetCycle.type !== FrequencyType.Monthly) {
                      add({
                        type: MetricType.KPI,
                        formulaType: DashboardSettingRateType.CompletionRate,
                      });
                    } else {
                      add({ formulaType: DashboardSettingRateType.CompletionRate });
                    }
                  }}
                >
                  + 添加仪表盘
                </Button>
              </div>
            )}
          </Form.List>
        </Form>
      </Drawer>
      <Spin spinning={isLoading} style={{ minHeight: '100vh' }}>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>{generateTitle()}</h2>
              <div
                style={{
                  height: '16px',
                  width: '1px',
                  backgroundColor: '#E8E8E8',
                  margin: '0 12px',
                }}
              />
              <span style={{ color: '#999', fontSize: '14px' }}>
                最新统计时间：{statisticFinishedAt}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Select
                value={departmentId}
                allowClear
                showSearch
                style={{ width: 180, marginRight: 8 }}
                options={departments.map((d: DepartmentItem) => ({ value: d.id, label: d.name }))}
                loading={isDepartmentsLoading || isUserLoading}
                filterOption={(input, option) => {
                  const label = option?.label?.toString().toLowerCase() || '';
                  return label.includes(input.toLowerCase());
                }}
                onChange={setDepartmentId}
              />
              <TargetCyclePicker
                targetCycle={selectedTargetCycle}
                onChange={handleTargetCycleChange}
              />
            </div>
          </div>
          {dashboardData?.settings?.length ? (
            <>
              {/* 正指标部分 */}
              {(() => {
                const positiveMetrics = (dashboardData?.settings || []).filter((setting) => {
                  const subject = subjectsData?.items.find(
                    (s: Subject) => s.id === setting.subjectId
                  );

                  return subject?.typeOfChange === TypeOfChange.Income;
                });
                return positiveMetrics.length > 0 ? (
                  <div style={{ marginBottom: '8px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#292C33',
                        }}
                      >
                        <SectionTitle title="正指标" />
                      </div>
                      <MetricCodeButton onClick={metricCodeDrawerOpen.onTrue} />
                    </div>
                    <Row gutter={[16, 16]}>
                      <DndProvider>
                        {positiveMetrics.map((c, index) => {
                          const planItem = planData?.find(
                            (item) => item.subject.id === c.subjectId
                          );
                          const varianceAnalysisId = planItem?.varianceAnalysisId;
                          const hasTarget =
                            planItem?.target != null && planItem?.target !== undefined;

                          const items = [
                            {
                              label: '仪表盘设置',
                              key: '1',
                              onClick: () => {
                                const settingsWithDefaults = dashboardData.settings.map(
                                  (setting) => ({
                                    ...setting,
                                    formulaType:
                                      setting.formulaType ||
                                      DashboardSettingRateType.CompletionRate,
                                  })
                                );
                                form.setFieldsValue({ settings: settingsWithDefaults });
                                formOpen.onTrue();
                              },
                            },
                          ];
                          // 如果有目标数据就显示差异分析选项，无论是达成率还是完成率
                          if (hasTarget) {
                            items.unshift({
                              label: '差异分析',
                              key: '0',
                              onClick: () => {
                                router.push(
                                  `${paths.dashboard.analysis.targetDifference.detail}?id=${varianceAnalysisId}`
                                );
                              },
                            });
                          }
                          return (
                            <Col
                              key={Math.random()}
                              style={{ position: 'relative', minWidth: '300px', flex: 1 }}
                            >
                              <DraggableItem disabled={disable} index={index} moveItem={moveItem}>
                                <Card
                                  style={{
                                    minHeight: 160,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: 'none',
                                  }}
                                  styles={{
                                    body: {
                                      width: '100%',
                                      borderRadius: '8px',
                                      padding: '24px 16px',
                                    },
                                  }}
                                >
                                  <Dropdown
                                    menu={{
                                      items,
                                    }}
                                    trigger={['click']}
                                  >
                                    <div className={styles.moreTag}>...</div>
                                  </Dropdown>
                                  {renderMetric(c)}
                                </Card>
                              </DraggableItem>
                            </Col>
                          );
                        })}
                      </DndProvider>
                    </Row>
                  </div>
                ) : null;
              })()}

              {/* 负指标部分 */}
              {(() => {
                const negativeMetrics = (dashboardData?.settings || []).filter((setting) => {
                  const subject = subjectsData?.items.find(
                    (s: Subject) => s.id === setting.subjectId
                  );
                  return subject?.typeOfChange === TypeOfChange.Expense;
                });

                return negativeMetrics.length > 0 ? (
                  <div style={{ marginTop: '16px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#292C33',
                        }}
                      >
                        <SectionTitle title="负指标" />
                      </div>
                    </div>
                    <Row gutter={[16, 16]}>
                      <DndProvider>
                        {negativeMetrics.map((c, index) => {
                          const planItem = planData?.find(
                            (item) => item.subject.id === c.subjectId
                          );
                          const varianceAnalysisId = planItem?.varianceAnalysisId;
                          const hasTarget =
                            planItem?.target != null && planItem?.target !== undefined;

                          const items = [
                            {
                              label: '仪表盘设置',
                              key: '1',
                              onClick: () => {
                                const settingsWithDefaults = dashboardData.settings.map(
                                  (setting) => ({
                                    ...setting,
                                    formulaType:
                                      setting.formulaType ||
                                      DashboardSettingRateType.CompletionRate,
                                  })
                                );
                                form.setFieldsValue({ settings: settingsWithDefaults });
                                formOpen.onTrue();
                              },
                            },
                          ];
                          // 如果有目标数据就显示差异分析选项，无论是达成率还是完成率
                          if (hasTarget) {
                            items.unshift({
                              label: '差异分析',
                              key: '0',
                              onClick: () => {
                                router.push(
                                  `${paths.dashboard.analysis.targetDifference.detail}?id=${varianceAnalysisId}`
                                );
                              },
                            });
                          }
                          return (
                            <Col
                              key={Math.random()}
                              style={{ position: 'relative', minWidth: '300px', flex: 1 }}
                            >
                              <DraggableItem disabled={disable} index={index} moveItem={moveItem}>
                                <Card
                                  style={{
                                    minHeight: 160,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: 'none',
                                  }}
                                  styles={{
                                    body: {
                                      width: '100%',
                                      borderRadius: '8px',
                                      padding: '24px 16px',
                                    },
                                  }}
                                >
                                  <Dropdown
                                    menu={{
                                      items,
                                    }}
                                    trigger={['click']}
                                  >
                                    <div className={styles.moreTag}>...</div>
                                  </Dropdown>
                                  {renderMetric(c)}
                                </Card>
                              </DraggableItem>
                            </Col>
                          );
                        })}
                      </DndProvider>
                    </Row>
                  </div>
                ) : null;
              })()}
            </>
          ) : (
            <>
              {/* 经营指标代码以及编码规则按钮 - 空状态时也显示 */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: '16px',
                }}
              >
                <MetricCodeButton onClick={metricCodeDrawerOpen.onTrue} />
              </div>

              <Flex
                align="center"
                justify="center"
                style={{ height: 'calc(100vh - 155px)', flexDirection: 'column' }}
              >
                <img
                  src={appViewEmpty}
                  style={{ width: '320px', height: '280px' }}
                  alt="appViewEmpty"
                />
                {!disable && (
                  <>
                    <div
                      style={{
                        color: '#525866',
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '24px',
                      }}
                    >
                      暂无数据，请创建专属视图，实时监控关键指标
                    </div>
                    <Button
                      onClick={() => {
                        form.resetFields();
                        form.setFieldsValue({
                          settings: [{ formulaType: DashboardSettingRateType.CompletionRate }],
                        });
                        formOpen.onTrue();
                      }}
                      type="primary"
                    >
                      开始配置
                    </Button>
                  </>
                )}
              </Flex>
            </>
          )}

          {/* 图表区域 */}
          {/* <Row gutter={16}>
        <Col span={12}>
          <Card title={<div style={{ borderLeft: '4px solid #1890ff', paddingLeft: '10px' }}>销售额同比对比</div>} style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={salesData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="本年" fill="#1890ff" />
                <Bar dataKey="上年" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col span={12}>
          <Card title={<div style={{ borderLeft: '4px solid #1890ff', paddingLeft: '10px' }}>销售综合分析</div>} style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart
                data={composedChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" orientation="left" stroke="#1890ff" />
                <YAxis yAxisId="right" orientation="right" stroke="#ff7300" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="销售量" barSize={20} fill="#1890ff" />
                <Line yAxisId="right" type="monotone" dataKey="同比增长" stroke="#ff7300" />
                <Area yAxisId="left" type="monotone" dataKey="目标达成率" fill="#8884d8" stroke="#8884d8" fillOpacity={0.3} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Card title={<div style={{ borderLeft: '4px solid #1890ff', paddingLeft: '10px' }}>毛利率趋势</div>} style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart
                data={profitData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="毛利率" stroke="#8884d8" activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row> */}

          {/* 新增图表区域 - 堆叠柱状图和饼图 */}
          {/* <Row gutter={16}>
        <Col span={12}>
          <Card
            title={<div style={{ borderLeft: '4px solid #1890ff', paddingLeft: '10px' }}>图形数据标题</div>}
            style={{ height: '450px' }}
            styles={{ body: { padding: '0 20px 20px' } }}
          >
            <ResponsiveContainer width="100%" height={380}>
              <BarChart
                data={stackedBarData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" axisLine={false} />
                <YAxis axisLine={false} />
                <Tooltip />
                <Legend wrapperStyle={{ bottom: 0 }} />
                <Bar dataKey="A组" stackId="a" fill="#FFCC33" />
                <Bar dataKey="B组" stackId="a" fill="#99CCFF" />
                <Bar dataKey="C组" stackId="a" fill="#6699FF" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={<div style={{ borderLeft: '4px solid #1890ff', paddingLeft: '10px' }}>图形数据标题</div>}
            style={{ height: '450px' }}
            styles={{ body: { padding: '0 20px 20px' } }}
          >
            <ResponsiveContainer width="100%" height={380}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={120}
                  paddingAngle={0}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  formatter={(value) => <span style={{ color: '#525866' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row> */}
        </Space>
      </Spin>

      <Drawer
        title="《经营指标代码以及编码规则2025年版》"
        placement="right"
        onClose={metricCodeDrawerOpen.onFalse}
        open={metricCodeDrawerOpen.value}
        width={800}
      >
        <div style={{ padding: '0 0 24px 0' }}>
          <SectionTitle title="经营指标代码" style={{ marginBottom: '16px' }} />

          <img
            src="/assets/subjects-table.png"
            alt="经营指标代码表"
            style={{ width: '100%', height: 'auto', marginBottom: '32px' }}
          />

          <div style={{ marginTop: '32px' }}>
            <SectionTitle title="编码规则" style={{ marginBottom: '16px' }} />
            <div
              style={{
                fontSize: '12px',
                color: '#525866',
                fontWeight: 400,
                marginBottom: '16px',
                lineHeight: '1.5',
              }}
            >
              示例：PI2025M1MCR_1，先确认需要查询的范围&quot;项目&quot;，再确认时间&quot;2025年月份1月&quot;，最后确认科目失目&quot;MCR
              月结占比&quot;，可以快速定位和搜索。
            </div>

            <img
              src="/assets/rules-table.png"
              alt="编码规则表"
              style={{ width: '100%', height: 'auto' }}
            />
          </div>

          <div style={{ marginTop: '32px' }}>
            <SectionTitle title="固定算法公式" style={{ marginBottom: '16px' }} />
            <Table
              dataSource={algorithmFormulaData}
              columns={algorithmFormulaColumns}
              pagination={false}
              size="small"
              bordered
              style={{ marginBottom: '16px' }}
            />
          </div>
        </div>
      </Drawer>
    </>
  );
}
